import { useContext, useEffect, useMemo } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIPolicyIcon from "@mui/icons-material/Policy";
import MUITypography from "@mui/material/Typography";
import MUIAlert from "@mui/material/Alert";
import MUIAlertTitle from "@mui/material/AlertTitle";
import MUITableContainer from "@mui/material/TableContainer";
import MUITable from "@mui/material/Table";
import MUITableHead from "@mui/material/TableHead";
import MUITableBody from "@mui/material/TableBody";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUICheckbox from "@mui/material/Checkbox";
import MUIBox from "@mui/material/Box";
import MUIGrid from "@mui/material/Grid";

import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUITooltip from "@mui/material/Tooltip";
import MUIInfoIcon from "@mui/icons-material/Info";

import Card from "../../Card";
import Progress from "../../Progress";
import InfoItem from "../../InfoItem";
import InsuranceSubType from "../../../Utility/Crud/InsuranceSubType";
import InsuranceDiscovery from "../../../Utility/InsuranceDiscovery";

/**
 * CardChartInsuranceDiscovery renders a Card component
 * displaying insurance discovery flow: Step 1 select patient, Step 2 select insurance, Step 3 review.
 * @returns {JSX.Element}
 */
const className = "CardChartInsuranceDiscovery";
const title = "Insurance Discovery";
const icon = MUIPolicyIcon;

export default function CardChartInsuranceDiscovery(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);

    const patient = chartContext.chart?.patient[chartContext.patientId] || null;

    const insuranceData = Array.isArray(chartContext.insuranceDiscoveryApiResults?.insuranceDiscoveryData)
        ? chartContext.insuranceDiscoveryApiResults?.insuranceDiscoveryData
        : chartContext.insuranceDiscoveryApiResults?.insuranceDiscoveryData
            ? [chartContext.insuranceDiscoveryApiResults.insuranceDiscoveryData]
            : [];

    /** Random emoji  */
    const wizardEmoji = useMemo(() => {
        const variants = [
            { base: "🧙", suffix: "" }, // Default
            { base: "🧙", suffix: "\u200D♀️" }, // Female
            { base: "🧙", suffix: "\u200D♂️" }, // Male
        ];
        const skinTones = [
            "\u{1F3FB}", // Light Skin Tone
            "\u{1F3FC}", // Medium-Light Skin Tone
            "\u{1F3FD}", // Medium Skin Tone
            "\u{1F3FF}", // Dark Skin Tone
        ];

        const { base, suffix } = variants[Math.floor(Math.random() * variants.length)];
        const tone = skinTones[Math.floor(Math.random() * skinTones.length)];
        return base + tone + suffix;
    }, []);

    /**
     * Resets selections when step changes.
     * Preserve selectedPatientIndex when navigating back from step 2 to step 1.
     * Preserve coverageType, insuranceType, and insuranceSubType when navigating between step 2 and step 3.
     */
    useEffect(() => {
        if (chartContext.insuranceDiscoveryActiveStep === 0) {
            chartContext.updateInsuranceDiscoverySelectedUser(null);
            chartContext.updateInsuranceDiscoverySelectedPatientIndex(null);
            chartContext.updateInsuranceDiscoveryCoverageTypes({});
            chartContext.updateInsuranceDiscoveryInsuranceOverrides({});
            chartContext.updateInsuranceDiscoveryAddressChanges(null);
        } else if (chartContext.insuranceDiscoveryActiveStep === 1) {
            chartContext.updateInsuranceDiscoverySelectedUser(null);
        }
    }, [chartContext.insuranceDiscoveryActiveStep]);

    /**
     * Auto-select patient when only one is returned in insuranceData.
     */
    useEffect(() => {
        if (chartContext.insuranceDiscoveryActiveStep === 1 && insuranceData.length === 1) {
            chartContext.updateInsuranceDiscoverySelectedPatientIndex(0);
        }
    }, [chartContext.insuranceDiscoveryActiveStep, insuranceData]);

    /**
     * Applies address fields from insurance discovery to the patient when the patient has no address.
     * Updates address, city, state, and zip code from the selected insurance discovery result.
     */
    useEffect(() => {
        const selectedPatientIndex = chartContext.insuranceDiscoverySelectedPatientIndex ?? (insuranceData.length === 1 ? 0 : null);
        const selectedInsuranceDiscoveryData = selectedPatientIndex != null ? insuranceData[selectedPatientIndex] : null;

        if (!patient || !selectedInsuranceDiscoveryData || !chartContext.onChange) {
            return;
        }

        /** If the patient has no address, apply the address fields from the selected insurance discovery result. */
        if (!patient.address) {
            const addressChangeFieldNames = [];
            const addressUpdate = {};

            if (selectedInsuranceDiscoveryData?.Address) {
                addressChangeFieldNames.push("Address");
                addressUpdate.address = selectedInsuranceDiscoveryData.Address;
            }
            if (selectedInsuranceDiscoveryData?.City) {
                addressChangeFieldNames.push("City");
                addressUpdate.city = selectedInsuranceDiscoveryData.City;
            }
            if (selectedInsuranceDiscoveryData?.State) {
                addressChangeFieldNames.push("State");
                addressUpdate.state = selectedInsuranceDiscoveryData.State;
            }
            if (selectedInsuranceDiscoveryData?.PostalCode) {
                addressChangeFieldNames.push("Zip Code");
                addressUpdate.zipCode = selectedInsuranceDiscoveryData.PostalCode;
            }

            if (addressChangeFieldNames.length > 0) {
                chartContext.updateInsuranceDiscoveryAddressChanges(addressChangeFieldNames);

                const payload = {
                    patient: { [chartContext.patientId]: addressUpdate }
                };
                chartContext.onChange(payload);
            }
        }
    }, [chartContext.insuranceDiscoverySelectedPatientIndex]);


    /**
     * Renders the patient row with box, checkbox, grid, and info items.
     * @param {Object} patientData - Patient data (PatientName, DOB, Gender, State)
     * @param {boolean} isSelected - Whether the patient is selected
     * @param {boolean} checkboxDisabled - If true, checkbox is shown but disabled (step 2)
     * @param {Function} onSelect - Called when row is clicked (step 1 only; ignored when checkboxDisabled)
     */
    const renderPatientRow = (patientData, isSelected, checkboxDisabled, onSelect) => {
        const boxSx = (theme) => ({
            p: 1.5,
            mb: checkboxDisabled ? 2 : 1,
            backgroundColor: theme.palette.background.default,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            ...(checkboxDisabled ? {} : { cursor: "pointer" }),
        });

        return (
            <MUIBox
                onClick={checkboxDisabled ? undefined : onSelect}
                sx={boxSx}
            >
                <MUICheckbox
                    size="small"
                    checked={isSelected}
                    disabled={checkboxDisabled}
                />
                <MUIBox sx={{ display: "flex", gap: 3, flexWrap: "wrap", flex: 1 }}>
                    <MUIGrid container spacing={3} rowSpacing={1}>
                        <MUIGrid item xs={"auto"}>
                            <InfoItem label="Name" value={patientData?.PatientName} />
                        </MUIGrid>
                        <MUIGrid item xs={"auto"}>
                            <InfoItem label="DOB" value={patientData?.DOB ? moment(patientData.DOB).format("M/D/YYYY") : null} />
                        </MUIGrid>
                        <MUIGrid item xs={"auto"}>
                            <InfoItem label="Gender" value={patientData?.Gender?.charAt?.(0)?.toUpperCase?.()} />
                        </MUIGrid>
                        <MUIGrid item xs={"auto"}>
                            <InfoItem label="State" value={patientData?.State} />
                        </MUIGrid>
                    </MUIGrid>
                </MUIBox>
            </MUIBox>
        );
    }

    /**
     * Renders the no insurance information alert (empty ComboPayerResponses).
     * @returns {JSX.Element} The no insurance information alert.
     */
    const renderNoInsuranceInformationAlert = () => {
        return (
            <MUIAlert severity="warning" sx={{ mt: 2 }}>
                <MUITypography variant="body2">
                    No insurance information found for this patient.
                </MUITypography>
            </MUIAlert>
        );
    };

    /**
     * Renders the no active insurances alert (insurances exist but none are active).
     * @returns {JSX.Element} The no active insurances alert.
     */
    const renderNoActiveInsurancesAlert = () => {
        return (
            <MUIAlert severity="warning" sx={{ mb: 2 }}>
                <MUIAlertTitle>No Active Insurance Policies</MUIAlertTitle>
                <MUITypography variant="body2">
                    Insurance discovery returned policies for this patient, but none are currently active. You cannot proceed with inactive policies.
                </MUITypography>
            </MUIAlert>
        );
    }

    /**
     * Renders the select patient step 1.
     * @returns {JSX.Element} The select patient step 1.
     */
    const renderSelectPatientStep1 = () => {
        const order = chartContext.orderId ? (chartContext.chart?.orderActivity?.[chartContext.orderId] || null) : null;
  
        const handleSelectPatient = (index) => {
            if (chartContext.insuranceDiscoverySelectedPatientIndex === index) {
                chartContext.updateInsuranceDiscoverySelectedPatientIndex(null);
            } else {
                chartContext.updateInsuranceDiscoverySelectedPatientIndex(index);
                chartContext.updateInsuranceDiscoveryCoverageTypes({});
                chartContext.updateInsuranceDiscoveryInsuranceOverrides({});
            }
        };

        // No patients found
        if (!insuranceData || insuranceData.length === 0) {
            return (
                <MUIAlert severity="error">
                    <MUIAlertTitle>No Patients Found</MUIAlertTitle>
                    <MUITypography variant="body2">
                        No insurance information was found for this patient. Please verify the patient information and try again.
                    </MUITypography>
                </MUIAlert>
            );
        }


        return (
            <MUIBox>
                {order?.isApproved === true && (
                    <MUIAlert severity="warning" sx={{ mb: 2, width: '100%' }}>
                        <MUITypography variant="subtitle2">This order has been approved and insurance cannot be updated.</MUITypography>
                    </MUIAlert>
                )}
                {insuranceData.map((data, patientIndex) => {
                    const insurances = data?.ComboPayerResponses || [];
                    const isPatientSelected = chartContext.insuranceDiscoverySelectedPatientIndex === patientIndex;

                    return (
                        <MUIBox key={patientIndex} sx={{ mb: 3 }}>
                            {renderPatientRow(data, isPatientSelected, false, () => handleSelectPatient(patientIndex))}

                            {/* Insurance List */}
                            {insurances.length > 0 ? (
                                <MUITableContainer>
                                    <MUITable size="small">
                                        <MUITableHead>
                                            <MUITableRow>
                                                <MUITableCell>Status</MUITableCell>
                                                <MUITableCell>PV Payor</MUITableCell>
                                                <MUITableCell>SP Payor</MUITableCell>
                                                <MUITableCell>Payor ID</MUITableCell>
                                                <MUITableCell>Policy #</MUITableCell>
                                                <MUITableCell>Group #</MUITableCell>
                                                <MUITableCell>Info</MUITableCell>
                                            </MUITableRow>
                                        </MUITableHead>
                                        <MUITableBody>
                                            {[...insurances]
                                                .sort((a, b) => {
                                                    const aActive = a?.Status?.toLowerCase() === "active";
                                                    const bActive = b?.Status?.toLowerCase() === "active";
                                                    return aActive === bActive ? 0 : (aActive ? -1 : 1);
                                                }).map((insurance) => {
                                                    return (
                                                        <MUITableRow
                                                            key={insurances.indexOf(insurance)}
                                                            sx={(theme) => insurance?.Status?.toLowerCase() !== "active" ? {
                                                                '& .MuiTableCell-root': {
                                                                    color: theme.palette.text.disabled
                                                                }
                                                            } : {}}
                                                        >
                                                            <MUITableCell>
                                                                <MUITypography
                                                                    variant="caption"
                                                                    sx={(theme) => ({
                                                                        fontWeight: 500,
                                                                        color: insurance?.Status?.toLowerCase() === "active" ? theme.palette.success.main : theme.palette.error.main,
                                                                    })}
                                                                >
                                                                    {insurance?.Status || "Unknown"}
                                                                </MUITypography>
                                                            </MUITableCell>
                                                            <MUITableCell>{insurance?.PayerName}</MUITableCell>
                                                            <MUITableCell>
                                                                {(() => {
                                                                    const matchingSubTypes = Object.values(mainContext.insuranceSubTypesIndexed || {}).filter(st => st.pvPayorId === insurance?.PayerCode);
                                                                    if (matchingSubTypes.length > 1) {
                                                                        return "Multiple";
                                                                    }
                                                                    const insuranceSubType = matchingSubTypes[0];
                                                                    if (!insuranceSubType) {
                                                                        return "Unknown";
                                                                    }
                                                                    const insuranceType = Object.values(mainContext.insuranceTypesIndexed || {}).find(type => type.id === insuranceSubType?.insuranceTypeId);
                                                                    const typeName = insuranceType?.name || "Unknown";
                                                                    const subTypeName = insuranceSubType?.name || "Unknown";
                                                                    return `${typeName} - ${subTypeName}`;
                                                                })()}
                                                            </MUITableCell>
                                                            <MUITableCell>{insurance?.PayerCode}</MUITableCell>
                                                            <MUITableCell>{insurance?.MemberID}</MUITableCell>
                                                            <MUITableCell>{insurance?.PlanCoverageSummary?.GroupNumber}</MUITableCell>
                                                            <MUITableCell>
                                                                <MUITooltip title={insurance?.PayerResponse?.AddtionalInfo ? insurance?.PayerResponse.AddtionalInfo : "N/A"} placement="right">
                                                                    <MUIInfoIcon fontSize="small" sx={(theme) => ({ color: theme.palette.primary.main })} />
                                                                </MUITooltip>
                                                            </MUITableCell>
                                                        </MUITableRow>
                                                    );
                                                })}
                                        </MUITableBody>
                                    </MUITable>
                                </MUITableContainer>
                            ) : (
                                renderNoInsuranceInformationAlert()
                            )}
                        </MUIBox>
                    );
                })}
            </MUIBox>
        );
    }

    /**
     * Renders the assign coverage type step 2.
     * @returns {JSX.Element} The assign coverage type step 2.
     */
    const renderAssignCoverageTypeStep2 = () => {
        const selectedPatientIndex = chartContext.insuranceDiscoverySelectedPatientIndex ?? 0;
        const selectedPatientData = insuranceData[selectedPatientIndex];

        const insurances = selectedPatientData?.ComboPayerResponses || [];

        /** Find all payor subTypes that match Payor ID (PayerCode). */
        const getMatchingInsuranceSubTypeOptions = (insurance) =>
            Object.values(mainContext.insuranceSubTypesIndexed || {})
                .filter(insuranceSubType => String(insuranceSubType.pvPayorId) === String(insurance?.PayerCode));

        /** Resolve insuranceTypeId + insuranceSubTypeId for a row: single match → use it; else use override. */
        const getResolvedInsuranceIds = (insurance, rowKey, overridesMap = chartContext.insuranceDiscoveryInsuranceOverrides || {}) => {
            const matching = getMatchingInsuranceSubTypeOptions(insurance);

            if (matching.length === 1) {
                return {
                    insuranceTypeId: matching[0].insuranceTypeId,
                    insuranceSubTypeId: matching[0].id
                };
            }

            const override = overridesMap[rowKey];

            return override ? {
                insuranceTypeId: override.insuranceTypeId,
                insuranceSubTypeId: override.insuranceSubTypeId
            } : {
                insuranceTypeId: null,
                insuranceSubTypeId: null
            }
        };

        /** Get insurance fields for a row. */
        const getInsuranceFields = (insurance, rowKey, overridesMap) => {
            const { insuranceTypeId, insuranceSubTypeId } = getResolvedInsuranceIds(insurance, rowKey, overridesMap);
            return {
                insuranceGroup: insurance?.PlanCoverageSummary?.GroupNumber || null,
                insurancePolicy: insurance?.MemberID || null,
                insuranceSubTypeId: insuranceSubTypeId || null,
                insuranceTypeId: insuranceTypeId || null,
            };
        };

        /** Get secondary insurance fields for a row. */
        const getSecondaryInsuranceFields = (insurance, rowKey, overridesMap) => {
            const { insuranceTypeId, insuranceSubTypeId } = getResolvedInsuranceIds(insurance, rowKey, overridesMap);
            return {
                secondaryInsuranceGroup: insurance?.PlanCoverageSummary?.GroupNumber || null,
                secondaryInsurancePolicy: insurance?.MemberID || null,
                secondaryInsuranceSubTypeId: insuranceSubTypeId || null,
                secondaryInsuranceTypeId: insuranceTypeId || null,
            };
        };

        const applyCoverageTypeToChart = (newCoverageTypes, overridesMap = chartContext.insuranceDiscoveryInsuranceOverrides || {}) => {
            // Use originalChart for revert values so we restore pre-discovery values when coverage type is changed/removed
            const originalDataSource = chartContext.orderId
                ? chartContext.originalChart?.orderActivity?.[chartContext.orderId]
                : chartContext.originalChart?.patient?.[chartContext.patientId];
            const update = {};

            insurances.forEach((insurance, idx) => {
                const key = `${selectedPatientIndex}-${idx}`;
                const coverageType = newCoverageTypes[key];
                if (coverageType === "primary") {
                    Object.assign(update, getInsuranceFields(insurance, key, overridesMap));
                } else if (coverageType === "secondary") {
                    Object.assign(update, getSecondaryInsuranceFields(insurance, key, overridesMap));
                }
                // Tertiary not stored in chart schema yet
            });

            // Only change what the user selected. When a coverage type is removed, revert to original chart values (before insurance discovery).
            const hasPrimary = Object.values(newCoverageTypes).includes("primary");
            const hasSecondary = Object.values(newCoverageTypes).includes("secondary");

            if (!hasPrimary) {
                update.insuranceGroup = originalDataSource?.insuranceGroup ?? null;
                update.insurancePolicy = originalDataSource?.insurancePolicy ?? null;
                update.insuranceSubTypeId = originalDataSource?.insuranceSubTypeId ?? null;
                update.insuranceTypeId = originalDataSource?.insuranceTypeId ?? null;
            }
            if (!hasSecondary) {
                update.secondaryInsuranceGroup = originalDataSource?.secondaryInsuranceGroup ?? null;
                update.secondaryInsurancePolicy = originalDataSource?.secondaryInsurancePolicy ?? null;
                update.secondaryInsuranceSubTypeId = originalDataSource?.secondaryInsuranceSubTypeId ?? null;
                update.secondaryInsuranceTypeId = originalDataSource?.secondaryInsuranceTypeId ?? null;
            }

            const order = chartContext.orderId ? (chartContext.chart?.orderActivity?.[chartContext.orderId] || null) : null;
            const openAndUnapprovedOrders = Object.values(chartContext.chart?.orderActivity || {}).filter(o => o?.isApproved !== true && o.status !== 4 && o.status !== 3); // 3 = Setup, 4 = Cancelled
            const payload = {};

            // Determine which entities to update based on context
            const isOnPatientView = !chartContext.orderId;
            const isNewOrder = order?.orderType === "New";

            if (isOnPatientView) {
                // Update patient and cascade to all open/unapproved orders
                payload.patient = { [chartContext.patientId]: update };

                if (openAndUnapprovedOrders.length > 0) {
                    payload.orderActivity = {};
                    openAndUnapprovedOrders.forEach((openOrder) => {
                        payload.orderActivity[openOrder.id] = { ...update };
                    });
                }
            } else if (isNewOrder) {
                // Update only the current "New" order
                payload.orderActivity = { [chartContext.orderId]: update };
            } else {
                // Update patient and cascade to all open/unapproved orders (including current)
                payload.patient = { [chartContext.patientId]: update };
                payload.orderActivity = {};
                openAndUnapprovedOrders.forEach((openOrder) => {
                    payload.orderActivity[openOrder.id] = { ...update };
                });
            }

            chartContext.onChange(payload);
        };

        const handleCoverageTypeChange = (rowKey, newCoverageType) => {
            const currentCoverageTypes = { ...chartContext.insuranceDiscoveryCoverageTypes || {} };
            const coverageTypeValue = newCoverageType || "";

            if (coverageTypeValue) {
                Object.keys(currentCoverageTypes).forEach((key) => {
                    if (currentCoverageTypes[key] === coverageTypeValue && key !== rowKey) {
                        delete currentCoverageTypes[key];
                    }
                });
            }
            if (coverageTypeValue) {
                currentCoverageTypes[rowKey] = coverageTypeValue;
            } else {
                delete currentCoverageTypes[rowKey];
            }
            chartContext.updateInsuranceDiscoveryCoverageTypes(currentCoverageTypes);
            applyCoverageTypeToChart(currentCoverageTypes);
        };

        /** Handle insurance override for a row. */
        const handleInsuranceOverride = (rowKey, insuranceTypeId, insuranceSubTypeId) => {
            const next = { ...chartContext.insuranceDiscoveryInsuranceOverrides || {} };
            if (insuranceTypeId != null) {
                next[rowKey] = { insuranceTypeId, insuranceSubTypeId: insuranceSubTypeId ?? null };
            } else {
                delete next[rowKey];
            }
            chartContext.updateInsuranceDiscoveryInsuranceOverrides(next);

            // When override is cleared or incomplete, also clear Coverage Type for this row
            const insuranceIndex = parseInt(rowKey.split("-")[1], 10);
            const insurance = insurances[insuranceIndex];
            const matchingInsuranceSubTypes = insurance ? getMatchingInsuranceSubTypeOptions(insurance) : [];

            const requiresOverride = matchingInsuranceSubTypes.length !== 1;
            const nextOverride = next[rowKey];
            const hasValidOverride = nextOverride?.insuranceTypeId != null && nextOverride?.insuranceSubTypeId != null;

            let nextCoverageTypes = { ...chartContext.insuranceDiscoveryCoverageTypes || {} };
            if (requiresOverride && !hasValidOverride && chartContext.insuranceDiscoveryCoverageTypes[rowKey]) {
                delete nextCoverageTypes[rowKey];
                chartContext.updateInsuranceDiscoveryCoverageTypes(nextCoverageTypes);
            }

            applyCoverageTypeToChart(nextCoverageTypes, next);
        };

        /** Get combined Insurance Type - Insurance Sub Type options for dropdown. */
        const getCombinedInsuranceTypeSubTypeOptions = () => {
            const types = mainContext.insuranceTypesIndexed || {};
            const subTypes = Object.values(mainContext.insuranceSubTypesIndexed || {});
            return InsuranceSubType.sort([...subTypes])
                .map((insuranceSubType) => {
                    const insuranceType = types[insuranceSubType.insuranceTypeId];
                    const typeName = insuranceType?.name || "Unknown";
                    const subTypeName = insuranceSubType.name || "Unknown";
                    return {
                        value: { insuranceTypeId: insuranceSubType.insuranceTypeId, insuranceSubTypeId: insuranceSubType.id },
                        title: `${typeName} - ${subTypeName}`,
                        key: `${insuranceSubType.insuranceTypeId}-${insuranceSubType.id}`,
                    };
                })
                .sort((a, b) => {
                    const aLower = a.title.toLowerCase();
                    const bLower = b.title.toLowerCase();
                    const aIsCashOrFacility = aLower.includes("cash") || aLower.includes("facility");
                    const bIsCashOrFacility = bLower.includes("cash") || bLower.includes("facility");

                    if (aIsCashOrFacility && !bIsCashOrFacility) return 1;
                    if (!aIsCashOrFacility && bIsCashOrFacility) return -1;
                    return a.title.localeCompare(b.title);
                });
        };

        /** Get SP insurance subtype dropdown options: 0 matches => all; 1 or 2+ matches => only those SP Payor matches. */
        const getInsuranceSubTypeDropdownOptions = (insurance) => {
            const matching = getMatchingInsuranceSubTypeOptions(insurance);
            const types = mainContext.insuranceTypesIndexed || {};
            if (matching.length === 0) {
                return getCombinedInsuranceTypeSubTypeOptions();
            }
            return matching.map((insuranceSubType) => {
                const insuranceType = types[insuranceSubType.insuranceTypeId];
                const typeName = insuranceType?.name || "Unknown";
                const subTypeName = insuranceSubType.name || "Unknown";
                return {
                    value: { insuranceTypeId: insuranceSubType.insuranceTypeId, insuranceSubTypeId: insuranceSubType.id },
                    title: `${typeName} - ${subTypeName}`,
                    key: `${insuranceSubType.insuranceTypeId}-${insuranceSubType.id}`,
                };
            }).sort((a, b) => a.title.localeCompare(b.title));
        };

        return (
            <MUIBox>
                {insurances.length > 0 && insurances.every((insurance) => insurance?.Status?.toLowerCase() !== "active") && renderNoActiveInsurancesAlert()}
                {renderPatientRow(selectedPatientData, true, true, undefined)}
                {insurances.length > 0 ? (
                    <MUITableContainer>
                        <MUITable size="small">
                            <MUITableHead>
                                <MUITableRow>
                                    <MUITableCell>Status</MUITableCell>
                                    <MUITableCell>PV Payor</MUITableCell>
                                    <MUITableCell sx={{ minWidth: "290px" }}>SP Payor</MUITableCell>
                                    <MUITableCell sx={{ whiteSpace: "nowrap" }}>Payor ID</MUITableCell>
                                    <MUITableCell>Policy #</MUITableCell>
                                    <MUITableCell>Group #</MUITableCell>
                                    <MUITableCell sx={{ minWidth: "170px" }}>Coverage Type</MUITableCell>
                                    <MUITableCell>Info</MUITableCell>
                                </MUITableRow>
                            </MUITableHead>
                            <MUITableBody>
                                {[...insurances]
                                    .map((insurance, idx) => ({ insurance, originalIndex: idx }))
                                    .sort((a, b) => {
                                        const aActive = a.insurance?.Status?.toLowerCase() === "active";
                                        const bActive = b.insurance?.Status?.toLowerCase() === "active";
                                        return aActive === bActive ? 0 : (aActive ? -1 : 1);
                                    })
                                    .map(({ insurance, originalIndex: insuranceIndex }) => {
                                        const rowKey = `${selectedPatientIndex}-${insuranceIndex}`;

                                        const isPrimaryTaken = Object.values(chartContext.insuranceDiscoveryCoverageTypes).includes("primary") && chartContext.insuranceDiscoveryCoverageTypes[rowKey] !== "primary";
                                        const isSecondaryTaken = Object.values(chartContext.insuranceDiscoveryCoverageTypes).includes("secondary") && chartContext.insuranceDiscoveryCoverageTypes[rowKey] !== "secondary";

                                        const isInactive = insurance?.Status?.toLowerCase() !== "active";
                                        const matchingInsuranceSubTypes = getMatchingInsuranceSubTypeOptions(insurance);

                                        const resolvedIds = getResolvedInsuranceIds(insurance, rowKey);

                                        // Require override when 0 or 2+ payors match; must select Insurance Type & Payor before Coverage Type
                                        const requiresOverride = matchingInsuranceSubTypes.length !== 1;
                                        const hasOverrideSelected = resolvedIds.insuranceTypeId != null && resolvedIds.insuranceSubTypeId != null;
                                        const coverageTypeBlocked = requiresOverride && !hasOverrideSelected;

                                        const spPayorDropdownOptions = getInsuranceSubTypeDropdownOptions(insurance);
                                        const selectedSpPayorOption = spPayorDropdownOptions
                                            .find((opt) => opt.value.insuranceTypeId === resolvedIds.insuranceTypeId && opt.value.insuranceSubTypeId === resolvedIds.insuranceSubTypeId) || null;

                                        return (
                                            <MUITableRow
                                                key={insuranceIndex}
                                                sx={(theme) => isInactive ? {
                                                    '& .MuiTableCell-root': {
                                                        color: theme.palette.text.disabled
                                                    }
                                                } : {}}
                                            >
                                                <MUITableCell>
                                                    <MUITypography
                                                        variant="caption"
                                                        sx={(theme) => ({
                                                            fontWeight: 500,
                                                            color: insurance?.Status?.toLowerCase() === "active" ? theme.palette.success.main : theme.palette.error.main,
                                                        })}
                                                    >
                                                        {insurance?.Status || "Unknown"}
                                                    </MUITypography>
                                                </MUITableCell>
                                                <MUITableCell>{insurance?.PayerName}</MUITableCell>
                                                <MUITableCell sx={{ minWidth: 200 }}>
                                                    <MUIAutocomplete
                                                        getOptionKey={option => option.key}
                                                        size="small"
                                                        fullWidth
                                                        disabled={matchingInsuranceSubTypes.length === 1 || isInactive}
                                                        options={spPayorDropdownOptions}
                                                        value={selectedSpPayorOption}
                                                        onChange={(e, newValue) => {
                                                            const ids = newValue?.value ?? null;
                                                            handleInsuranceOverride(
                                                                rowKey,
                                                                ids?.insuranceTypeId ?? null,
                                                                ids?.insuranceSubTypeId ?? null
                                                            );
                                                        }}
                                                        getOptionLabel={(option) => option.title}
                                                        isOptionEqualToValue={(option, value) =>
                                                            option.value.insuranceTypeId === value?.value?.insuranceTypeId &&
                                                            option.value.insuranceSubTypeId === value?.value?.insuranceSubTypeId
                                                        }
                                                        renderInput={(params) => (
                                                            <MUITextField {...params}
                                                                InputLabelProps={{ shrink: true }}
                                                                size="small" variant="standard"
                                                                fullWidth
                                                            />
                                                        )}
                                                    />
                                                </MUITableCell>
                                                <MUITableCell>{insurance?.PayerCode}</MUITableCell>
                                                <MUITableCell>{insurance?.MemberID}</MUITableCell>
                                                <MUITableCell>{insurance?.PlanCoverageSummary?.GroupNumber}</MUITableCell>
                                                <MUITableCell>
                                                    <MUIAutocomplete
                                                        size="small"
                                                        fullWidth
                                                        disabled={isInactive || coverageTypeBlocked}
                                                        value={chartContext.insuranceDiscoveryCoverageTypes[rowKey] || null}
                                                        onChange={(event, newValue) => handleCoverageTypeChange(rowKey, newValue || "")}
                                                        options={["primary", "secondary"]}
                                                        getOptionLabel={(option) => option ? option.charAt(0).toUpperCase() + option.slice(1) : ""}
                                                        getOptionDisabled={(option) => {
                                                            if (option === "primary") { return isPrimaryTaken; }
                                                            if (option === "secondary") { return isSecondaryTaken; }
                                                            return false;
                                                        }}
                                                        renderInput={(params) => <MUITextField {...params}
                                                            InputLabelProps={{ shrink: true }}
                                                            size="small"
                                                            variant="standard"
                                                            fullWidth
                                                        />}
                                                        sx={{ minWidth: 120 }}
                                                    />
                                                </MUITableCell>
                                                <MUITableCell>
                                                    <MUITooltip title={insurance?.PayerResponse?.AddtionalInfo ? insurance?.PayerResponse.AddtionalInfo : "N/A"} placement="right">
                                                        <MUIInfoIcon fontSize="small" sx={(theme) => ({ color: theme.palette.primary.main })} />
                                                    </MUITooltip>
                                                </MUITableCell>
                                            </MUITableRow>
                                        );
                                    })}
                            </MUITableBody>
                        </MUITable>
                    </MUITableContainer>
                ) : renderNoInsuranceInformationAlert()}
            </MUIBox>
        );
    }

    /**
     * Determines which view to render based on step and edit mode state.
     * @returns {JSX.Element} Either edit view or read-only view
     */
    const renderContent = () => {
        if (chartContext.insuranceDiscoveryIsLoading) {
            return (
                <Progress message="Loading..." />
            );
        }

        // Step 0: Let's get started or error state for missing required fields
        if (chartContext.insuranceDiscoveryActiveStep === 0) {
            const missingFields = InsuranceDiscovery.getInsurnaceDiscoveryMissingPatientFields(patient);

            return (
                <MUIAlert
                    severity={missingFields.length === 0 ? "info" : "error"}
                    icon={
                        missingFields.length === 0 ? (
                            <span style={{ fontSize: "3rem" }}>{wizardEmoji}</span>
                        ) : undefined
                    }
                >
                    <MUIAlertTitle>{missingFields.length === 0 ? "Let's Get Started" : "Missing Required Fields"}</MUIAlertTitle>
                    <MUITypography variant="body2">
                        {missingFields.length === 0
                            ? "This wizard helps you find insurance policies associated with the patient. Results may be incomplete or inaccurate, so review them carefully before\u00A0continuing."
                            : `Missing required fields: ${missingFields.join(", ")}. Please add these fields to the patient chart before running insurance\u00A0discovery.`
                        }
                    </MUITypography>
                </MUIAlert>
            );
        }

        // Step 1: Error state
        if (chartContext.insuranceDiscoveryApiResults?.status === "error") {
            return (
                <MUIAlert severity="error"
                >
                    <MUIAlertTitle>Insurance Discovery Error</MUIAlertTitle>
                    <MUITypography variant="body2" sx={{ mb: 2 }}>
                        {chartContext.insuranceDiscoveryApiResults?.detail?.errorDescription}
                    </MUITypography>
                </MUIAlert>
            );
        }

        // Step 1: Patient selection table
        if (chartContext.insuranceDiscoveryActiveStep === 1) {
            return renderSelectPatientStep1();
        }

        // Step 2: Assign Coverage Type: Insurance selection for selected patient + Primary or Secondary coverage
        if (chartContext.insuranceDiscoveryActiveStep === 2) {
            return renderAssignCoverageTypeStep2();
        }

        return null;
    };

    /** Render the card. */
    return (
        <Card
            renderContent={renderContent}
            getTitle={() => title}
        />
    );
}

CardChartInsuranceDiscovery.meta = { className: className, title: title, icon: icon }
